import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card, CardTitle } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { ConfirmDialog } from '@shared/ui/ConfirmDialog';
import { SkeletonLine } from '@shared/ui/Skeleton';
import {
  useApproveKadryProfileMutation,
  useCreateKadryProfileDraftMutation,
  useListKadryProfilesQuery,
  useUpdateKadryProfileMutation,
} from '@shared/api';
import {
  KADRY_PROFILE_ORIGIN_LABELS,
  kadryBlankProfile,
  kadryDateTime,
  kadryProfileIssues,
  useKadryAccess,
  type KadryHiringRequest,
  type KadryProfileVersion,
  type KadrySearchProfile,
  type KadryWorkSite,
} from '@entities/kadry';
import {
  KadryCardHead,
  KadryEmpty,
  KadryErrorBanner,
  KadryFormActions,
  KadryMuted,
  KadryPerson,
  KadryProfileStatusBadge,
} from '../ui';
import { ProfileEditor, kadryIsProfileFieldError } from './ProfileEditor';
import { ProfileView } from './ProfileView';

const Versions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const VersionTab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Dot = styled.span<{ $tone: 'success' | 'warning' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme, $tone }) => theme.colors.status[$tone]};
`;

const VersionMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const IssueList = styled.ul`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  padding-left: ${({ theme }) => theme.spacing(5)};
`;

const Gap = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

type Mode = { kind: 'view' } | { kind: 'edit' } | { kind: 'new'; blank: KadrySearchProfile };

interface Props {
  request: KadryHiringRequest;
  workSites: KadryWorkSite[];
  /** Перечитать заявку (после конфликта версий). */
  onRefreshRequest: () => void;
}

/**
 * Профиль поиска заявки (ТЗ §3 С1, Прил. Е): версии, черновик (вручную или
 * копией утверждённой), правка с If-Match по ревизии и утверждение. Утверждённая
 * версия неизменяема; поиск идёт только по ней. «Изменить профиль» у
 * утверждённой — сразу черновик-копия действующей версии в редакторе.
 */
export function ProfilePanel({ request, workSites, onRefreshRequest }: Props) {
  const { canWrite } = useKadryAccess();
  const { data: versions, isLoading, error, refetch } = useListKadryProfilesQuery(request.id);
  const [selected, setSelected] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'view' });
  const [dirty, setDirty] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  // Ревизия черновика, сохранённого кнопкой «Сохранить и утвердить» (список
  // версий ещё не перечитан, а утверждать надо именно её).
  const [approveRevision, setApproveRevision] = useState<number | null>(null);

  const [createDraft, createState] = useCreateKadryProfileDraftMutation();
  const [updateDraft, updateState] = useUpdateKadryProfileMutation();
  const [approve, approveState] = useApproveKadryProfileMutation();

  // Править и утверждать: recruiter/hr_admin с полным доступом, заявка не закрыта.
  // Без права записи на агента («только чтение») — только просмотр.
  const canEdit =
    canWrite('profile_approve') && request.access === 'full' && request.status !== 'closed';

  const list = useMemo(() => versions ?? [], [versions]);
  const draft = list.find((v) => v.status === 'draft');
  const approved = list.filter((v) => v.status === 'approved');
  const current: KadryProfileVersion | undefined =
    list.find((v) => v.version === selected) ??
    draft ??
    list.find((v) => v.version === request.active_profile_version) ??
    list[0];

  // Выбранная версия исчезла (например, после обновления) — показать по умолчанию.
  useEffect(() => {
    if (selected != null && !list.some((v) => v.version === selected)) setSelected(null);
  }, [list, selected]);

  const refreshAll = useCallback(() => {
    updateState.reset();
    approveState.reset();
    createState.reset();
    refetch();
    onRefreshRequest();
  }, [updateState, approveState, createState, refetch, onRefreshRequest]);

  const onDirtyChange = useCallback((d: boolean) => setDirty(d), []);
  // Форма закрыта — несохранённых изменений больше нет.
  useEffect(() => {
    if (mode.kind !== 'edit') setDirty(false);
  }, [mode.kind]);

  async function onCreateManual(profile: KadrySearchProfile) {
    try {
      const pv = await createDraft({
        requestId: request.id,
        body: { mode: 'manual', profile },
      }).unwrap();
      setSelected(pv.version);
      setMode({ kind: 'edit' });
    } catch {
      // Ошибка — в createState.error.
    }
  }

  async function onCopy(fromVersion: number) {
    try {
      const pv = await createDraft({
        requestId: request.id,
        body: { mode: 'copy', from_version: fromVersion },
      }).unwrap();
      setSelected(pv.version);
      setMode({ kind: 'edit' });
    } catch {
      // Ошибка — в createState.error (баннер над профилем).
    }
  }

  async function onSave(profile: KadrySearchProfile, baseRevision: number | undefined) {
    if (!current || current.status !== 'draft') return;
    try {
      await updateDraft({
        requestId: request.id,
        version: current.version,
        // Ревизия, с которой начата правка, а не последняя загруженная: если
        // черновик успели сохранить в другом месте — сервер ответит конфликтом.
        revision: baseRevision ?? current.revision,
        profile,
      }).unwrap();
    } catch {
      // Ошибка — в updateState.error.
    }
  }

  /** Сохранить черновик и сразу открыть подтверждение утверждения. */
  async function onSaveAndApprove(profile: KadrySearchProfile, baseRevision: number | undefined) {
    if (!current || current.status !== 'draft') return;
    try {
      const pv = await updateDraft({
        requestId: request.id,
        version: current.version,
        revision: baseRevision ?? current.revision,
        profile,
      }).unwrap();
      setApproveRevision(pv.revision);
      setApproveOpen(true);
    } catch {
      // Ошибка — в updateState.error.
    }
  }

  async function onApprove() {
    if (!current || current.status !== 'draft') return;
    try {
      await approve({
        requestId: request.id,
        version: current.version,
        revision: approveRevision ?? current.revision,
      }).unwrap();
      setApproveOpen(false);
      setApproveRevision(null);
      setMode({ kind: 'view' });
      setSelected(current.version);
    } catch {
      setApproveOpen(false);
      setApproveRevision(null);
    }
  }

  /**
   * Главная кнопка панели: черновик есть — открыть его в редакторе; есть
   * только утверждённые — черновик-копия действующей версии сразу в редакторе;
   * версий нет — пустая форма.
   */
  function startEdit() {
    createState.reset();
    if (draft) {
      setSelected(draft.version);
      setMode({ kind: 'edit' });
    } else if (approved.length > 0) {
      void onCopy(request.active_profile_version ?? approved[0].version);
    } else {
      setMode({ kind: 'new', blank: kadryBlankProfile(request.title, request.work_site_id) });
    }
  }

  const draftIssues = current?.status === 'draft' ? kadryProfileIssues(current.profile) : [];
  const actionError = updateState.error ?? approveState.error ?? createState.error;
  // Ошибка проверки конкретного поля показывается под ним в форме, не баннером.
  const bannerError = kadryIsProfileFieldError(actionError) ? undefined : actionError;

  let body;
  if (isLoading) {
    body = (
      <>
        <SkeletonLine $width="50%" />
        <SkeletonLine />
        <SkeletonLine $width="70%" />
      </>
    );
  } else if (error) {
    body = <KadryErrorBanner error={error} onRefresh={refetch} title="Не удалось загрузить профиль" />;
  } else if (mode.kind === 'new') {
    body = (
      <>
        <Gap>
          <KadryMuted>
            Новый черновик профиля. Заполните, что знаете: неполный черновик можно сохранить и
            дополнить позже, утвердить — только полный.
          </KadryMuted>
        </Gap>
        <ProfileEditor
          initial={mode.blank}
          workSites={workSites}
          busy={createState.isLoading}
          submitLabel="Создать черновик"
          allowPristineSubmit
          serverError={createState.error}
          onSubmit={onCreateManual}
          onCancel={() => {
            createState.reset();
            setMode({ kind: 'view' });
          }}
          errorSlot={
            createState.error && !kadryIsProfileFieldError(createState.error) ? (
              <Gap>
                <KadryErrorBanner
                  error={createState.error}
                  onRefresh={refreshAll}
                  onDismiss={() => createState.reset()}
                />
              </Gap>
            ) : null
          }
        />
      </>
    );
  } else if (!current) {
    body = (
      <KadryEmpty>
        Профиль поиска ещё не составлен.
        {canEdit
          ? ' Создайте черновик: укажите должность, навыки, допуски, график и источники.'
          : ' Его составит и утвердит отдел кадров.'}
      </KadryEmpty>
    );
  } else {
    const isDraft = current.status === 'draft';
    const editing = mode.kind === 'edit' && isDraft && canEdit;
    body = (
      <>
        {list.length > 1 && (
          <Versions role="tablist" aria-label="Версии профиля">
            {list.map((v) => (
              <VersionTab
                key={v.version}
                type="button"
                role="tab"
                aria-selected={v.version === current.version}
                $active={v.version === current.version}
                onClick={() => {
                  if (dirty && !window.confirm('Есть несохранённые изменения. Перейти без сохранения?')) {
                    return;
                  }
                  setSelected(v.version);
                  setMode({ kind: 'view' });
                }}
              >
                <Dot $tone={v.status === 'approved' ? 'success' : 'warning'} />
                Версия {v.version}
                {v.version === request.active_profile_version ? ' · действующая' : ''}
              </VersionTab>
            ))}
          </Versions>
        )}
        <VersionMeta>
          <strong>Версия {current.version}</strong>
          <KadryProfileStatusBadge status={current.status} />
          {current.version === request.active_profile_version && (
            <Badge $tone="info">Действующая</Badge>
          )}
          <span>
            {KADRY_PROFILE_ORIGIN_LABELS[current.origin]} · создана {kadryDateTime(current.created_at)}
            {current.created_by && (
              <>
                {', '}
                <KadryPerson id={current.created_by} />
              </>
            )}
          </span>
          {current.status === 'approved' && (
            <span>
              утвердил <KadryPerson id={current.approved_by} /> · {kadryDateTime(current.approved_at)}
            </span>
          )}
          {isDraft && !editing && <span>изменён {kadryDateTime(current.updated_at)}</span>}
        </VersionMeta>

        {bannerError ? (
          <Gap>
            <KadryErrorBanner
              error={bannerError}
              onRefresh={refreshAll}
              onDismiss={() => {
                updateState.reset();
                approveState.reset();
                createState.reset();
              }}
            />
          </Gap>
        ) : null}

        {editing ? (
          <ProfileEditor
            initial={current.profile}
            revision={current.revision}
            workSites={workSites}
            busy={updateState.isLoading || approveState.isLoading}
            submitLabel="Сохранить черновик"
            serverError={updateState.error}
            onSubmit={onSave}
            onCancel={() => setMode({ kind: 'view' })}
            onDirtyChange={onDirtyChange}
            onSubmitAndApprove={onSaveAndApprove}
          />
        ) : (
          <>
            <ProfileView profile={current.profile} workSites={workSites} />
            {isDraft && canEdit && (
              <>
                {draftIssues.length > 0 && (
                  <Gap>
                    <KadryMuted as="div">
                      Перед утверждением нужно дополнить:
                      <IssueList>
                        {draftIssues.map((i) => (
                          <li key={i.field + i.message}>{i.message}</li>
                        ))}
                      </IssueList>
                    </KadryMuted>
                  </Gap>
                )}
                <KadryFormActions>
                  <Button type="button" $variant="secondary" onClick={() => setMode({ kind: 'edit' })}>
                    Редактировать
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setApproveOpen(true)}
                    disabled={draftIssues.length > 0 || approveState.isLoading}
                  >
                    Утвердить версию
                  </Button>
                </KadryFormActions>
              </>
            )}
          </>
        )}
      </>
    );
  }

  const editingNow = mode.kind === 'new' || (mode.kind === 'edit' && current?.status === 'draft');
  const headAction = draft
    ? 'Редактировать'
    : approved.length > 0
      ? 'Изменить профиль'
      : 'Составить профиль';

  return (
    <Card>
      <KadryCardHead>
        <CardTitle>Профиль поиска</CardTitle>
        {canEdit && !editingNow && !isLoading && !error && (
          <Button
            type="button"
            $size="sm"
            onClick={startEdit}
            disabled={createState.isLoading}
            title={
              !draft && approved.length > 0
                ? 'Откроется копия действующей версии — после правки её нужно утвердить'
                : undefined
            }
          >
            {createState.isLoading ? 'Открываем…' : headAction}
          </Button>
        )}
      </KadryCardHead>
      {body}

      <ConfirmDialog
        open={approveOpen}
        title={`Утвердить версию ${current?.version ?? ''}`}
        warning={
          request.status === 'active'
            ? 'Версия станет действующей: поиск по заявке сразу перезапустится по ней. Следующие правки — кнопкой «Изменить профиль».'
            : 'Версия станет действующей: поиск пойдёт по ней, когда заявку запустят в работу. Следующие правки — кнопкой «Изменить профиль».'
        }
        confirmText="утвердить"
        confirmLabel="Введите «утвердить» для подтверждения"
        confirmButtonLabel="Утвердить"
        busyLabel="Утверждаем…"
        isBusy={approveState.isLoading}
        onConfirm={onApprove}
        onCancel={() => {
          setApproveOpen(false);
          setApproveRevision(null);
        }}
      />
    </Card>
  );
}
